import { Navigate } from '@tanstack/react-router'
import { useState } from 'react'
import { Alert, Field, Input, Mono } from '../components/ui'
import { ApiError, errorMessage } from '../lib/api'
import { formatDate, humanize } from '../lib/format'
import { useOutboxEmails } from '../lib/participants'
import { useMe } from '../lib/session'

/** Demo-mode rehearsal aid: read the emails the API would have sent. Tex ops_admin only. */
export function OutboxPage() {
  const me = useMe()
  const [to, setTo] = useState('')
  const [applied, setApplied] = useState<string | undefined>(undefined)
  const emails = useOutboxEmails(applied, me.data?.tex_role === 'ops_admin')

  if (me.data === null) return <Navigate to="/sign-in" />
  if (!me.data) return <p className="text-sm text-neutral-500">Loading…</p>
  if (me.data.tex_role !== 'ops_admin') return <Alert tone="error">Only Tex ops admins can read the outbox.</Alert>

  return (
    <section className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold text-neutral-900">Email outbox</h1>
        <p className="mt-1 text-sm text-neutral-500">
          Messages the API queued in demo mode. Nothing is delivered; use this to pick up magic links and borrower share links
          during a rehearsal.
        </p>
      </div>

      <form
        onSubmit={(e) => {
          e.preventDefault()
          setApplied(to.trim().toLowerCase() || undefined)
        }}
        className="max-w-md"
      >
        <Field label="Recipient" hint="Leave blank for the most recent messages to anyone.">
          <Input type="email" value={to} onChange={(e) => setTo(e.target.value)} placeholder="dana@bluebonnet.example" />
        </Field>
      </form>

      {emails.isPending && <p className="text-sm text-neutral-500">Loading…</p>}
      {emails.isError && (
        <Alert tone="error">
          {emails.error instanceof ApiError && emails.error.status === 404 ? 'The outbox preview is only available in demo mode.' : errorMessage(emails.error)}
        </Alert>
      )}
      {emails.data && emails.data.emails.length === 0 && <Alert tone="info">No messages{applied ? ` for ${applied}` : ''}.</Alert>}
      {emails.data && emails.data.emails.length > 0 && (
        <ul className="space-y-3">
          {emails.data.emails.map((m) => (
            <li key={m.id} className="rounded-xl border border-[var(--border-subtle)] bg-white shadow-sm p-4 text-sm">
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <div>
                  <span className="font-medium">{m.subject}</span>
                  <span className="ml-2 text-xs text-neutral-500">
                    {humanize(m.kind)} · to <Mono>{m.to}</Mono> · {humanize(m.status)}
                  </span>
                </div>
                <span className="text-xs text-neutral-400">{formatDate(m.created_at)}</span>
              </div>
              <pre className="mt-2 whitespace-pre-wrap break-all font-mono text-xs text-neutral-700">{m.text}</pre>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
